import React, { CSSProperties } from 'react';

/**
 * IconDisc — the centered icon surface that sits at the top of a bottom-sheet
 * body, above the title. Same visual language as the biometric-prompt disc in
 * `components/RegistrationPrototype/d4Primitives.tsx` (`IconDisc` + its
 * `disc.bg` / `disc.border` / `disc.stroke` tokens): a subtly-tinted hollow
 * CIRCLE with an orange hairline border, an orange glyph inside — NOT a
 * filled colored surface. Lifted here so every drawer in the app renders its
 * lead glyph identically.
 *
 * `active` is a state hook: when true, the disc picks up a soft orange glow
 * ring so the state change is felt even before any animation registers.
 * Combine with `animation="bell-shake"` to also swing the disc like a bell
 * hanging from a hook. `prefers-reduced-motion` disables the swing but keeps
 * the border + glow change so the state is still legible.
 */

type Props = {
  /**
   * The glyph to render inside the disc. Callers pass a fully-configured
   * element (e.g. an MUI `NotificationsActiveIcon` with a size/color already
   * set). Match the disc's stroke colour (`#ffb45c`) so the family reads
   * consistently across every prompt.
   */
  icon: React.ReactNode;
  /** Highlighted state — brighter border + soft orange glow. */
  active?: boolean;
  /**
   * Motion accent. `bell-shake` swings the disc from a top-center pivot on a
   * 1.4s infinite loop; suppressed under `prefers-reduced-motion`. Omit
   * (or pass `none`) to leave the disc still.
   */
  animation?: 'bell-shake' | 'none';
  /**
   * Optional size override in px. Defaults to 64 — the biometric-prompt
   * baseline the tournament + registration flows both use.
   */
  size?: number;
};

const IconDisc: React.FC<Props> = ({ icon, active = false, animation = 'none', size = 64 }) => {
  const shake = animation === 'bell-shake';
  return (
    <div style={wrapStyle}>
      <div
        style={{
          ...discStyle,
          ...(active ? discActiveStyle : null),
          width: size,
          height: size
        }}
        className={shake ? 'icd-bell-shake' : undefined}
        aria-hidden>
        {icon}
      </div>
      {/* Keyframes scoped as inline <style> so callers don't have to touch a
          global stylesheet. Rendered only when the animation is on. */}
      {shake ? <style>{BELL_SHAKE_KEYFRAMES}</style> : null}
    </div>
  );
};

export default IconDisc;

// ---------------------------------------------------------------------------
// Styles — mirror d4Primitives' disc tokens (Hijack skin):
//   bg:     rgba(232,130,31,.13)
//   border: 1px solid rgba(232,130,31,.4)
//   stroke: #ffb45c (caller sets on the glyph)
// ---------------------------------------------------------------------------

const wrapStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center'
};

const discStyle: CSSProperties = {
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Subtle orange-tinted fill (matches the sheet's dark surface almost
  // perfectly) with a hairline orange border — the biometric prompt look.
  background: 'rgba(232, 130, 31, 0.13)',
  border: '1px solid rgba(232, 130, 31, 0.4)',
  // Top-center pivot so a bell-shake looks like a bell swinging from its hook.
  transformOrigin: 'top center',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
};

const discActiveStyle: CSSProperties = {
  borderColor: 'rgba(232, 130, 31, 0.85)',
  boxShadow: '0 0 0 4px rgba(232, 130, 31, 0.18), 0 0 20px rgba(232, 130, 31, 0.35)'
};

const BELL_SHAKE_KEYFRAMES = `
@keyframes icd-bell-shake {
  0%   { transform: rotate(0deg); }
  10%  { transform: rotate(-14deg); }
  20%  { transform: rotate(12deg); }
  30%  { transform: rotate(-10deg); }
  40%  { transform: rotate(8deg); }
  50%  { transform: rotate(-6deg); }
  60%  { transform: rotate(4deg); }
  70%  { transform: rotate(-3deg); }
  80%  { transform: rotate(2deg); }
  90%  { transform: rotate(-1deg); }
  100% { transform: rotate(0deg); }
}
.icd-bell-shake {
  animation: icd-bell-shake 1.4s cubic-bezier(0.36, 0.07, 0.19, 0.97) infinite;
}
@media (prefers-reduced-motion: reduce) {
  .icd-bell-shake { animation: none; }
}
`;
